/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import {
  Box,
  Breadcrumbs,
  FormControl,
  Select,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import React, { useState, useMemo, useCallback } from "react";
import { NavigableBreadcrumbLink } from "../../../components/page_breadcrumbs/navigable_breadcrumb_link";
import api from "../../../api/index.js";
import authenticatedFetch from "../../../api/authenticatedFetch";
import { MenuHeading } from "../../../components/menu_heading";
import camelize from "../../../utils/camelize.js";
import { Loader } from "../../../components/loader";
import { IndexTable } from "../../../components/index_table";
import ClearIcon from "@mui/icons-material/Clear";
import CheckIcon from "@mui/icons-material/Check";
import {
  StyledBreadCrumbContainer,
  StyledLink,
} from "../../user_management/styles";
import { selectBaseStyles, viewTypographyStyles, StyledSearchBar } from "../styles";
import { UpdateButton } from "../../../components/update_button";
import { FilterContainer } from "../../../components/filter_container";
import { StyledInputLabel } from "../../restaurant/styles.jsx";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import DropdownSearchBar from "../../../components/dorpdown_searchbar/index.jsx";
import { DropdownInfiniteLoader } from "../../../components/dropdown_infinite_loader";
import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { ResetButton } from "../../../components/reset_button/index.jsx";
import { formatDate } from "../../../utils/formatDate.js"
import { SelectedMenuItem } from '../../../components/selected_menu_item/index.jsx'
import { buildQueryParams } from "../../../utils/buildQueryParams.js";
import { normalizeSearchTerm } from "../../../utils/normalizeSearchTerm";
import { useNotificationContext } from "../../../contexts/notificationContext.jsx";
import { OrderAppliedDiscountModal } from "../../../components/order_discount_modal/index.jsx";
import SearchIcon from '@mui/icons-material/Search';
import { StyledMenuItem } from "../../../components/styled_menu_item/index.jsx";
import { hasSingleItem } from "../../../utils/hasSingleItem.js";
import { getSingleItemValue } from "../../../utils/getSingleItemValue";
import { Query_Keys } from "../../../lib/queryKeys.js";
import { OrderNumberCell } from "../../../components/order_number_cell";
import { PerPage } from "../../../constants/perPage";
import {
  getDropdownNextPageParam,
  hasDisplayableDropdownValue,
  mergeUniqueByKey,
  normalizeEmployee,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../../utils/dropdownInfiniteQuery";
import { useDebounce } from "../../../utils/useDebounce";
import { useInfiniteScrollObserver } from "../../../hooks/useInfiniteScrollObserver";
import { isValidDayjsValue } from "../../../utils/isValidDayjsValue";
import { isValidCustomDateRange } from "../../../utils/isValidCustomDateRange";

const ALL_VALUE = 'All';

const datePickerStyles = {
  ".MuiInputBase-root": {
    height: "2.4rem",
    backgroundColor: "white",
    fontSize: "14px",
  },
  "& input::placeholder": {
    color: "black",
  },
  width: 160,
};

const defaultFilter = {
  restaurant_ids: [ALL_VALUE],
  restaurant_group_id: ALL_VALUE,
  employee_name: ALL_VALUE,
  dateFilter: ALL_VALUE,
  orderType: ALL_VALUE,
  customDateFrom: null,
  customDateTo: null,
}

export const TipCompliance = () => {
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [orderBy, setOrderBy] = useState("date_of_transaction");
  const [order, setOrder] = useState("desc");
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [filters, setFilters] = useState(defaultFilter);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilter)
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [restaurantSearch, setRestaurantSearch] = useState("");
  const [selectedRestaurantIds, setSelectedRestaurantIds] = useState([ALL_VALUE])
  const [selectedRestaurantGroup, setSelectedRestaurantGroup] = useState(ALL_VALUE)
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState('')
  const queryClient = useQueryClient();
  const [csvLoading, setCsvLoading] = useState(false)
  const showNotification = useNotificationContext();
  const [selectedOrder, setSelectedOrder] = useState('')
  const [orderOpen, setOrderOpen] = useState('')
  const [search, setSearch] = useState('')
  const normalizedSearchTerm = useMemo(() => normalizeSearchTerm(search), [search])
  const debouncedSearchTerm = useDebounce(normalizedSearchTerm, 300)
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([])
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([])
  const [employeeOptionsCache, setEmployeeOptionsCache] = useState([])
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300)
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300)
  const debouncedEmployeeSearch = useDebounce(employeeSearch.trim(), 300)

  const OrderTypes = [
    "OwnersTable",
    "Non-OwnersTable",
  ];

  const fetchRestaurantGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    });
    return response?.data?.map(normalizeRestaurantGroup) || [];
  };

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({ search: debouncedRestaurantGroupSearch }),
    queryFn: fetchRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchEmployees = async ({ pageParam = 1 }) => {
    const payload = {
      page: pageParam,
      per_page: PerPage,
      search: debouncedEmployeeSearch || undefined,
      restaurant_id: filters?.restaurant_ids?.includes(ALL_VALUE) ? undefined : filters?.restaurant_ids,
      restaurant_group_id: selectedRestaurantGroup === ALL_VALUE ? undefined : selectedRestaurantGroup
    }
    const response = await api.get('/v1/admin/dropdowns/active_guests_list', {params: payload})
    return response?.data
      ?.map(normalizeEmployee)
      ?.filter((employee) => (
        hasDisplayableDropdownValue(employee?.label)
        && hasDisplayableDropdownValue(employee?.value)
      )) || []
  }

  const {
    data: employees = [],
    isFetching: employeeFetching,
    isFetchingNextPage: employeesFetchingNextPage,
    fetchNextPage: fetchNextEmployeesPage,
    hasNextPage: hasNextEmployeesPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.active_guests_list({
      restaurant_group_id: selectedRestaurantGroup,
      restaurant_id: filters?.restaurant_ids,
      search: debouncedEmployeeSearch,
    }),
    queryFn: fetchEmployees,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const fetchRestaurants = async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/restaurants_list', {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
        restaurant_group_ids: selectedRestaurantGroup === ALL_VALUE ? undefined : selectedRestaurantGroup
      }
    })
    return response?.data?.map(normalizeRestaurant) || []
  }

  const {
    data: restaurants = [],
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: selectedRestaurantGroup,
      search: debouncedRestaurantSearch,
    }),
    queryFn: fetchRestaurants,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  })

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
    isFetchingNextPage: restaurantsFetchingNextPage,
  })

  const { rootRef: employeeMenuRef, targetRef: employeeLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextEmployeesPage,
    hasNextPage: hasNextEmployeesPage,
    isFetchingNextPage: employeesFetchingNextPage,
  })

  const fetchComplianceTip = async () => {
    const newFormattedFilters = formattedFilters(appliedFilters, debouncedSearchTerm);
    const response = await api.get('/v1/admin/reports/tip_compliance', {
      params: {
        ...newFormattedFilters,
        per_page: itemsPerPage,
        page: page,
        sort_by: orderBy,
        sort_order: order,
      },
    })

    const result = {
      data: response?.data,
      pageCount: response?.headers["total-count"]
    }
    return result
  }

  const { data: queryResult, isFetching } = useQuery({
    queryKey: Query_Keys.tip_compliance({
      queryFilter: appliedFilters,
      page: page,
      itemsPerPage: itemsPerPage,
      orderBy,
      order,
      search: debouncedSearchTerm,
    }),
    queryFn: fetchComplianceTip,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const data = queryResult?.data;

  const fetchOrderAppliedDiscount = async () => {
    const response = await api.get(`/v1/admin/orders/${selectedOrder}/detail`)
    return response?.data
  }

  const { data: orderData, isLoading: orderLoading } = useQuery({
    queryKey: Query_Keys.applied_discount({
      selected_order: selectedOrder
    }),
    queryFn: fetchOrderAppliedDiscount,
    staleTime: 5 * 60 * 1000,
    enabled: selectedOrder !== ''
  })

  React.useEffect(() => {
    setRestaurantOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurants, 'id'));
  }, [restaurants]);

  React.useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurantGroups, 'label'));
  }, [restaurantGroups]);

  React.useEffect(() => {
    setEmployeeOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, employees, 'value'));
  }, [employees]);

  const hasSingleAvailableRestaurantGroup = !debouncedRestaurantGroupSearch && hasSingleItem(restaurantGroups);
  const hasSingleAvailableRestaurant = !debouncedRestaurantSearch && hasSingleItem(restaurants);
  const hasSingleAvailableEmployee = !debouncedEmployeeSearch && hasSingleItem(employees);

  React.useEffect(() => {
    if (hasSingleAvailableRestaurantGroup) {
      const singleRestaurantGroup = getSingleItemValue(restaurantGroups);
      setFilters(prev => ({
        ...prev,
        restaurant_group_id: singleRestaurantGroup.label,
        restaurant_ids: [ALL_VALUE],
        employee_name: ALL_VALUE,
      }));
      setSelectedRestaurantGroup(singleRestaurantGroup.label)
      setSelectedRestaurantIds([ALL_VALUE])
    }
  }, [hasSingleAvailableRestaurantGroup, restaurantGroups]);

  React.useEffect(() => {
    if (hasSingleAvailableRestaurant) {
      const singleRestaurant = getSingleItemValue(restaurants);
      setFilters(prev => ({
        ...prev,
        restaurant_ids: [singleRestaurant.id],
        employee_name: ALL_VALUE,
      }));
      setSelectedRestaurantIds([singleRestaurant.id])
    }
  }, [hasSingleAvailableRestaurant, restaurants]);

  React.useEffect(() => {
    if (hasSingleAvailableEmployee) {
      const singleEmployee = getSingleItemValue(employees);
      setFilters(prev => ({
        ...prev,
        employee_name: singleEmployee.value
      }));
    }
  }, [employees, hasSingleAvailableEmployee]);

  const formattedFilters = (sourceFilters = filters, searchTerm = '') => {
    const params = {};
    const today = dayjs();

    // Date range handling
    if (sourceFilters.dateFilter !== "Custom Date" && sourceFilters.dateFilter !== ALL_VALUE) {
      switch (sourceFilters.dateFilter) {
        case "Today":
          params.date_range = {
            from: today.format("DD/MM/YYYY"),
            to: today.format("DD/MM/YYYY"),
          };
          break;
        case "Last 7 Days":
          params.date_range = {
            from: today.subtract(7, "day").format("DD/MM/YYYY"),
            to: today.format("DD/MM/YYYY"),
          };
          break;
        case "Yesterday":
          params.date_range = {
            from: today.subtract(1, "day").format("DD/MM/YYYY"),
            to: today.subtract(1, "day").format("DD/MM/YYYY"),
          };
          break;
        case "This Week":
          params.date_range = {
            from: today.startOf("week").format("DD/MM/YYYY"),
            to: today.endOf("week").format("DD/MM/YYYY"),
          };
          break;
        case "This Month":
          params.date_range = {
            from: today.startOf("month").format("DD/MM/YYYY"),
            to: today.endOf("month").format("DD/MM/YYYY"),
          };
          break;
        case "Last Month":
          params.date_range = {
            from: today
              .subtract(1, "month")
              .startOf("month")
              .format("DD/MM/YYYY"),
            to: today.subtract(1, "month").endOf("month").format("DD/MM/YYYY"),
          };
          break;
      }
    } else if (
      sourceFilters.dateFilter === "Custom Date" &&
      isValidCustomDateRange(sourceFilters.customDateFrom, sourceFilters.customDateTo)
    ) {
      params.date_range = {
        from: dayjs(sourceFilters.customDateFrom).format("DD/MM/YYYY"),
        to: dayjs(sourceFilters.customDateTo).format("DD/MM/YYYY"),
      };
    }

    if (!sourceFilters?.restaurant_ids?.includes(ALL_VALUE))
      params.restaurant_ids = sourceFilters?.restaurant_ids;

    if (sourceFilters?.employee_name !== ALL_VALUE) {
      params.network_guest_name = getEmployeeName(sourceFilters?.employee_name);
    }

    if (sourceFilters?.restaurant_group_id !== ALL_VALUE)
      params.restaurant_group_id = sourceFilters?.restaurant_group_id

    if (sourceFilters.orderType === "OwnersTable") {
      params.has_ot_discount = true;
    } else if (sourceFilters.orderType === "Non-OwnersTable") {
      params.has_ot_discount = false;
    }

    if (searchTerm) {
      params.search = searchTerm
    }

    return params;
  };

  const getRestaurantGroupName = (id) => {
    if (id === ALL_VALUE) return 'All';
    const restaurantGroup = restaurantGroupOptionsCache?.find((res) => res.label === id)
    return restaurantGroup?.value ? restaurantGroup?.value  : ''
  }

  const getRestaurantDisplayValue = () => {
    if (!filters?.restaurant_ids || filters?.restaurant_ids?.length === 0) {
      return 'All';
    }

    if (filters?.restaurant_ids?.includes(ALL_VALUE)) {
      return 'All';
    }

    if (filters?.restaurant_ids?.length === 1 && restaurantOptionsCache) {
      const restaurant = restaurantOptionsCache.find(r => r.id === filters.restaurant_ids[0]);
      return restaurant ? restaurant.name : 'All';
    }

    return `${filters?.restaurant_ids?.length} Restaurants Selected`;
  }

  const getEmployeeDisplayValue = () => {
    if (hasSingleAvailableEmployee) {
      return getSingleItemValue(employees)?.label || 'All';
    }

    if (filters?.employee_name === ALL_VALUE) {
      return 'All';
    }

    return getEmployeeName(filters?.employee_name);
  }

  const getEmployeeName = (employeeId) => {
    if (!employeeId || employeeId === ALL_VALUE) {
      return 'All';
    }

    const employee = employeeOptionsCache?.find((option) => `${option?.value}` === `${employeeId}`)
      || employees?.find((option) => `${option?.value}` === `${employeeId}`);

    return employee?.label || employeeId;
  }

  const handleEmployeeName = (event) => {
    setFilters({ ...filters, employee_name: event.target.value });
    setFiltersChanged(true);
  };

  const handleEmployeeClear = () => {
    setFilters({ ...filters, employee_name: ALL_VALUE });
    setFiltersChanged(true);
  }

  const handleDateRangeClear = () => {
    setFilters({ ...filters, dateFilter: ALL_VALUE, customDateFrom: null, customDateTo: null });
    setFiltersChanged(true)
  }

  const handleRestaurantGroupChange = (event) => {
    setFilters({...filters, restaurant_group_id: event.target.value, restaurant_ids: [ALL_VALUE], employee_name: ALL_VALUE})
    setSelectedRestaurantGroup(event.target.value)
    setSelectedRestaurantIds([ALL_VALUE])
    setFiltersChanged(true)
  }

  const handleRestaurantName = (event) => {
    const values = event.target.value;

    if ((values?.length === 1 && values.includes(ALL_VALUE)) || values?.length === 0) {
      setFilters({ ...filters, restaurant_ids: [ALL_VALUE], employee_name: ALL_VALUE });
      setSelectedRestaurantIds([ALL_VALUE])
    }
    else {
      const newRestaurantIds = values?.filter(val => val !== ALL_VALUE);
      setFilters({ ...filters, restaurant_ids: newRestaurantIds, employee_name: ALL_VALUE });
      setSelectedRestaurantIds(newRestaurantIds)
    }
    setFiltersChanged(true);
  };

  const handleRestaurantGroupClear = () => {
    setFilters({...filters, restaurant_group_id: ALL_VALUE, restaurant_ids: [ALL_VALUE], employee_name: ALL_VALUE})
    setSelectedRestaurantGroup(ALL_VALUE)
    setSelectedRestaurantIds([ALL_VALUE])
    setFiltersChanged(true)
  }

  const handleRestaurantClear = () => {
    setFilters({ ...filters, restaurant_ids: [ALL_VALUE], employee_name: ALL_VALUE });
    setSelectedRestaurantIds([ALL_VALUE])
    setFiltersChanged(true);
  }

  const handleOrderTypeClear = () => {
    setFilters({
      ...filters,
      orderType: ALL_VALUE,
    });
    setFiltersChanged(true);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const nextFilters = {
        ...prev,
        [field]: value,
      };

      if (field === "dateFilter" && value !== "Custom Date") {
        nextFilters.customDateFrom = null;
        nextFilters.customDateTo = null;
      }

      if (field === "customDateFrom") {
        if (!value) {
          nextFilters.customDateTo = null;
        } else if (isValidDayjsValue(value) && !isValidDayjsValue(prev.customDateTo)) {
          nextFilters.customDateTo = dayjs();
        }
      }

      return nextFilters;
    });
    setFiltersChanged(true);
  };

  const handleSearch = useCallback((searchQuery) => {
    setSearch(searchQuery)
    setPage(1)
  }, [])

  const handleChange = () => {
    setFiltersChanged(false);
    setAppliedFilters(filters)
    setItemsPerPage(25);
    setPage(1);
    setRestaurantSearch('')
  };

  const handleReset = () => {
    setFiltersChanged(false)
    setAppliedFilters(defaultFilter)
    setFilters(defaultFilter)
    setSearch('')
    setItemsPerPage(25);
    setPage(1);
    setSelectedRestaurantIds([ALL_VALUE])
    setSelectedRestaurantGroup(ALL_VALUE)
    setOrderBy('date_of_transaction')
    setOrder('desc')
  }

  const isCustomDateRangeValid =
    filters?.dateFilter !== "Custom Date" ||
    isValidCustomDateRange(filters?.customDateFrom, filters?.customDateTo);

  const isUpdateDisabled =
    filters?.dateFilter === "Custom Date" &&
    !isCustomDateRangeValid;

  const handleSortChange = (columnKey, sortDirection) => {
    setOrderBy(columnKey);
    setOrder(sortDirection);
    setPage(1);
  };

  const handleOrderOpen = (order_id) => {
    setOrderOpen(true)
    setSelectedOrder(order_id)
  }

  const handleOrderClose = () => {
    setOrderOpen(false)
  }

  const handleExportCsv = async () => {
    try {
      setCsvLoading(true);
      const newFormattedFilters = formattedFilters(appliedFilters, debouncedSearchTerm);
      const query = buildQueryParams(newFormattedFilters)
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=tip_compliance&${query}`;

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      let date = new Date
      let filename = `tip_compliance_${dayjs(date).format('YYYY-MM-DD')}.csv`

      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(downloadUrl);

      showNotification('CSV downloaded successfully', 'success');
    } catch (error) {
      console.error('CSV download error:', error);
      showNotification(error?.message || 'Failed to download CSV', 'error');
    } finally {
      setCsvLoading(false);
    }
  }

  const Days = [
    "Today",
    "Yesterday",
    "This Week",
    "Last 7 Days",
    "This Month",
    "Last Month",
    "Custom Date",
  ];

  const tableColumns = [
    "order_number",
    "order_details",
    "employee",
    "restaurant_name",
    "compliance",
    "compliance_threshold_%",
    "bill_amount",
    "check_in_code",
    "amount",
    "discount",
    "tip_paid",
    "tip_%",
    "date_of_transaction",
  ];

  const columns = tableColumns?.map((column) => {
    if (column === "order_number") {
      return {
        key: column,
        label: "Order #",
      };
    }

    if (column === "discount") {
      return {
        key: column,
        label: "Perq",
      };
    }

    return {
      key: column,
      label: camelize(column),
      sortable: column !== "order_details",
    };
  });

  const TableRows = (data) => {
    return data?.map((record) => {
      return {
        order_number: record?.order_no,
        order_details: (
          <OrderNumberCell
            orderValue={record?.order_id}
            onView={handleOrderOpen}
          />
        ),
        employee: record?.network_guest_name ? record?.network_guest_name : '',
        restaurant_name: record?.restaurant,
        compliance: {
          display: (
            <Box
              sx={{
                backgroundColor: record?.compliance
                  ? "rgba(46, 125, 50, 1)"
                  : "rgba(211, 47, 47, 1)",
                borderRadius: "50%",
                height: "20px",
                width: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {record?.compliance ? (
                <CheckIcon sx={{ color: "white", fontSize: "1.2rem" }} />
              ) : (
                <ClearIcon sx={{ color: "white", fontSize: "1.2rem" }} />
              )}
            </Box>
          ),
          sortValue: record?.compliance
        },
        "compliance_threshold_%": `${Number(record?.tip_compliance_threshold_percent).toFixed(2)}%`,
        bill_amount: record?.total ? `$${Number(record?.total).toFixed(2) ?? '0.00'}`: "$0.00",
        check_in_code: record?.check_in_code,
        amount: record?.amount ? `$${Number(record?.amount).toFixed(2) ?? '0.00'}` : "$0.00",
        discount: `$${Number(record?.discount).toFixed(2) ?? '0.00'}`,
        tip_paid: record?.tip ? `$${Number(record?.tip).toFixed(2) ?? '0.00'}` : "$0.00",
        "tip_%":
          record?.tip_percent === null ? "0.00%" : `${Number(record?.tip_percent).toFixed(2) ?? '0.00'}%`,
        date_of_transaction: formatDate(record?.paid, record?.time_zone_abbr),
        order_id: record?.order_id
      };
    });
  };

  const rows = TableRows(data);

  return (
    <React.Fragment>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          overflowY: "scroll",
          overflowX: 'hidden',
        }}
      >
        <StyledBreadCrumbContainer>
          <Breadcrumbs>
            <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
            <StyledLink color={"#515152"}>Reports</StyledLink>
            <StyledLink color={"#515152"}>Perq Reports</StyledLink>
            <StyledLink color={"#515152"}>Tip Compliance Report</StyledLink>
          </Breadcrumbs>
        </StyledBreadCrumbContainer>

        <FilterContainer>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: { xs: 2, md: 0 }
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: "18px" },
                flexWrap: "wrap",
                width: { xs: "100%", md: "auto" },
                flex: 1
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  ...viewTypographyStyles,
                  alignSelf: { xs: "flex-start", sm: "center" },
                  whiteSpace: "nowrap"
                }}
              >
                View
              </Typography>

              <FormControl
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "160px" }
                }}
              >
                <StyledInputLabel>Restaurant Group</StyledInputLabel>
                <Select
                  size="small"
                  label="Restaurant Group"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { ref: restaurantGroupMenuRef, sx: { maxHeight: "40vh" } },
                    autoFocus: false,
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: { xs: "100%", sm: "160px" },
                    width: { xs: "100%", sm: "160px" },
                    borderColor: "#2b4fb9",
                  }}
                  onChange={handleRestaurantGroupChange}
                  onClose={() => setTimeout(() => setRestaurantGroupSearch(''), 1000)}
                  value={hasSingleAvailableRestaurantGroup
                    ? getSingleItemValue(restaurantGroups)?.label
                    : (selectedRestaurantGroup || ALL_VALUE)}
                  renderValue={() =>{
                      if (hasSingleAvailableRestaurantGroup) {
                        return getSingleItemValue(restaurantGroups)?.value || 'All';
                      }
                      return getRestaurantGroupName(selectedRestaurantGroup);
                  }}
                  endAdornment = {
                    selectedRestaurantGroup !== ALL_VALUE && !hasSingleAvailableRestaurantGroup ? (
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                      <IconButton
                        edge='end'
                        size="small"
                        disableRipple
                        data-testid="restaurant-group-clear-btn"
                        onClick={handleRestaurantGroupClear}
                      >
                        <ClearIcon sx={{fontSize: '20px'}}/>
                      </IconButton>
                    </InputAdornment>
                    ) : (
                      null
                    )
                  }
                >
                  <DropdownSearchBar changeSearchText={setRestaurantGroupSearch} placeholder={'Search restaurant group'} isFetching={restaurantGroupFetching} />
                  {/* All option */}
                  {!hasSingleAvailableRestaurantGroup && (<StyledMenuItem
                    value={ALL_VALUE}
                  >
                    {selectedRestaurantGroup === ALL_VALUE ? <SelectedMenuItem data="All" /> : "All"}
                  </StyledMenuItem>
                  )}
                  {
                    restaurantGroups
                      .map((res) => (
                        <StyledMenuItem
                          key={res?.label}
                          value={res?.label}
                        >
                          {
                            hasSingleAvailableRestaurantGroup || selectedRestaurantGroup === res?.label ? <SelectedMenuItem data={res?.value} /> : res?.value
                          }
                        </StyledMenuItem>
                    ))
                  }
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextRestaurantGroupsPage}
                    targetRef={restaurantGroupLoaderRef}
                  />
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "200px" }
                }}
              >
                <StyledInputLabel>Restaurant</StyledInputLabel>
                <Select
                  multiple
                  label="Restaurant"
                  value={hasSingleAvailableRestaurant
                    ? [getSingleItemValue(restaurants)?.id]
                    : (selectedRestaurantIds || [ALL_VALUE])
                  }
                  size="small"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { ref: restaurantMenuRef, sx: { maxHeight: "40vh" } },
                    autoFocus: false,
                     MenuListProps: { sx: { pt: 0 } },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: { xs: "100%", sm: "200px" },
                    borderColor: "#2b4fb9",
                  }}
                  onChange={handleRestaurantName}
                  onClose={() => setTimeout(() => setRestaurantSearch(''), 1000)}
                  renderValue={() => {
                    if (hasSingleAvailableRestaurant) {
                      return getSingleItemValue(restaurants)?.name || 'All';
                    }
                    return getRestaurantDisplayValue();
                  }}
                  endAdornment= {
                    !selectedRestaurantIds?.includes(ALL_VALUE) && !hasSingleAvailableRestaurant ? (
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 23 }}>
                      <IconButton
                        edge='end'
                        size="small"
                        disableRipple
                        data-testid="restaurant-clear-btn"
                        onClick={handleRestaurantClear}
                      >
                        <ClearIcon sx={{fontSize: '20px'}}/>
                      </IconButton>
                    </InputAdornment>
                    ) : (
                      null
                    )
                  }
                >
                  <DropdownSearchBar changeSearchText={setRestaurantSearch} placeholder={'Search restaurant'} isFetching={restaurantFetching} />
                  {/* All option */}
                  {!hasSingleAvailableRestaurant && (<StyledMenuItem
                    key={ALL_VALUE}
                    value={ALL_VALUE}
                    disabled={!selectedRestaurantIds?.includes(ALL_VALUE)}
                  >
                    {selectedRestaurantIds?.includes(ALL_VALUE) ? <SelectedMenuItem data="All" /> : "All"}
                  </StyledMenuItem>
                  )}
                  {
                    restaurants
                      .map((res) => (
                        <StyledMenuItem
                          key={res?.id}
                          value={res?.id}
                        >
                          {
                            hasSingleAvailableRestaurant || selectedRestaurantIds?.includes(res?.id) ? (
                              <SelectedMenuItem data={res?.name} />
                            ) : (
                              <>
                                {res?.name}
                              </>
                            )
                          }
                        </StyledMenuItem>
                    ))
                  }
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextRestaurantsPage}
                    targetRef={restaurantLoaderRef}
                  />
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "200px" }
                }}
              >
                <StyledInputLabel>Employee Name</StyledInputLabel>
                <Select
                  value={hasSingleAvailableEmployee
                    ? getSingleItemValue(employees)?.value
                    : (filters?.employee_name || ALL_VALUE)}
                  renderValue={() => {
                      return getEmployeeDisplayValue();
                    }
                  }
                  label="Employee Name"
                  size="small"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { ref: employeeMenuRef, sx: { maxHeight: "40vh" } },
                    autoFocus: false,
                     MenuListProps: { sx: { pt: 0 } },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: { xs: "100%", sm: "200px" },
                    borderColor: "#2b4fb9",
                  }}
                  onChange={handleEmployeeName}
                  onClose={() => setTimeout( () => setEmployeeSearch(""), 1000)}
                  endAdornment= {
                    filters?.employee_name !== ALL_VALUE && !hasSingleAvailableEmployee ? (
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 23 }}>
                      <IconButton
                        edge='end'
                        size="small"
                        disableRipple
                        data-testid="employee-clear-btn"
                        onClick={handleEmployeeClear}
                      >
                        <ClearIcon sx={{fontSize: '20px'}}/>
                      </IconButton>
                    </InputAdornment>
                    ) : (
                      null
                    )
                  }
                >
                  <DropdownSearchBar changeSearchText={setEmployeeSearch} placeholder={'Search employee'} isFetching={employeeFetching} />
                  {/* All option */}
                  {!hasSingleAvailableEmployee && (<StyledMenuItem
                    value={ALL_VALUE}
                  >
                    {filters?.employee_name === ALL_VALUE ? <SelectedMenuItem data="All" /> : "All"}
                  </StyledMenuItem>
                  )}
                  {employees
                    ?.map((employee) => (
                      <StyledMenuItem
                        key={employee?.value}
                        value={employee?.value}
                      >
                        {
                          hasSingleAvailableEmployee || filters?.employee_name === employee?.value ? <SelectedMenuItem data={employee?.label} /> : employee?.label
                        }
                      </StyledMenuItem>
                    ))}
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextEmployeesPage}
                    targetRef={employeeLoaderRef}
                  />
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "160px" }
                }}
              >
                <StyledInputLabel>Date Range</StyledInputLabel>
                <Select
                  value={filters?.dateFilter || ALL_VALUE}
                  renderValue={() =>
                    filters?.dateFilter
                  }
                  label="Date Range"
                  size="small"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { sx: { width: '250px' } },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: { xs: "100%", sm: "160px" },
                    borderColor: "#2b4fb9",
                  }}
                  onChange={(e) =>
                    handleFilterChange("dateFilter", e.target.value)
                  }
                  endAdornment={
                    filters?.dateFilter !== ALL_VALUE ? (
                      <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                        <IconButton
                          edge='end'
                          size="small"
                          disableRipple
                          data-testid="date-range-clear-btn"
                          onClick={handleDateRangeClear}
                        >
                          <ClearIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                      </InputAdornment>
                    ) : (
                      null
                    )
                  }
                >
                  {/* All option */}
                  <StyledMenuItem
                    value={ALL_VALUE}
                  >
                    {filters?.dateFilter === ALL_VALUE ? <SelectedMenuItem data="All" /> : "All"}
                  </StyledMenuItem>
                  {Days?.map((value) => (
                    <StyledMenuItem
                      key={value}
                      value={value}
                    >
                      {
                        filters?.dateFilter === value ? <SelectedMenuItem data={value} /> : value
                      }
                    </StyledMenuItem>
                  ))}
                </Select>
              </FormControl>

              {filters.dateFilter === "Custom Date" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                    width: { xs: "100%", sm: "auto" }
                  }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Typography variant="body2" sx={viewTypographyStyles}>
                      Days
                    </Typography>
                    <DatePicker
                      format="MM-DD-YYYY"
                      sx={{
                        ...datePickerStyles,
                        width: { xs: "100%", sm: "140px", lg: '160px' }
                      }}
                      slotProps={{ textField: { size: "small" } }}
                      value={filters.customDateFrom || null}
                      onChange={(date) =>
                        handleFilterChange("customDateFrom", date)
                      }
                    />
                    <Typography variant="body2" sx={viewTypographyStyles}>
                      through
                    </Typography>
                    <DatePicker
                      sx={{
                        ...datePickerStyles,
                        width: { xs: "100%", sm: "140px", lg: '160px' }
                      }}
                      format="MM-DD-YYYY"
                      slotProps={{ textField: { size: "small" } }}
                      value={filters.customDateTo || null}
                      onChange={(date) =>
                        handleFilterChange("customDateTo", date)
                      }
                    />
                  </LocalizationProvider>
                </Box>
              )}

              <FormControl
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "170px" },
                }}
              >
                <StyledInputLabel>Order Type</StyledInputLabel>

                <Select
                  value={filters.orderType}
                  label="Order Type"
                  size="small"
                  sx={{
                    ...selectBaseStyles,
                    minWidth: { xs: "100%", sm: "170px" },
                    borderColor: "#2b4fb9",
                  }}
                  onChange={(e) => handleFilterChange("orderType", e.target.value)}
                  renderValue={() => filters.orderType}
                  endAdornment={
                    filters.orderType !== ALL_VALUE ? (
                      <InputAdornment
                        position="end"
                        sx={{ position: "absolute", right: 25 }}
                      >
                        <IconButton
                          edge="end"
                          size="small"
                          disableRipple
                          onClick={handleOrderTypeClear}
                        >
                          <ClearIcon sx={{ fontSize: "20px" }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                >
                  <StyledMenuItem value={ALL_VALUE}>
                    {filters.orderType === ALL_VALUE ? (
                      <SelectedMenuItem data="All" />
                    ) : (
                      "All"
                    )}
                  </StyledMenuItem>

                  {OrderTypes.map((type) => (
                    <StyledMenuItem key={type} value={type}>
                      {filters.orderType === type ? (
                        <SelectedMenuItem data={type} />
                      ) : (
                        type
                      )}
                    </StyledMenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                alignSelf: { xs: "flex-start", md: "center" },
                mt: { xs: 1, md: 0 },
                width: { xs: "100%", md: "auto" },
                justifyContent: { xs: "flex-start", md: "flex-end" }
              }}
            >
              <ResetButton
                onClick={handleReset}
                status={isFetching}
              />

              <UpdateButton
                onClick={handleChange}
                filtersChanged={filtersChanged}
                status={isFetching}
                disabled={isUpdateDisabled}
              />
            </Box>
          </Box>
        </FilterContainer>
        <MenuHeading
          report={false}
          dividerStyle={{ marginBottom: '16px' }}
          rightComponent={
            <StyledSearchBar
              value={search}
              placeholder="Search tip compliance"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              onChange={(e) => handleSearch(e.target.value)}
              autoComplete="off"
              inputProps={{ 'data-testid': 'tip-compliance-search-input' }}
            />
          }
        >
          Tip Compliance Report
        </MenuHeading>

        {(isFetching && !debouncedSearchTerm) ? (
          <Loader />
        ) : (
          <IndexTable
            columns={columns}
            data={rows}
            tableId="tip-compliance"
            type="entries"
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            pageCount={queryResult?.pageCount}
            serverSort={true}
            onSortChange={handleSortChange}
            serverOrderBy={orderBy}
            serverOrder={order}
            report={false}
            showDownloadButton={true}
            disableDownloadButton={data?.length === 0 || csvLoading}
            handleExportCsv={handleExportCsv}
            csvLoading={csvLoading}
            handleOpen={handleOrderOpen}
            emptyStateText="No Data Exists"
            searchLoading={isFetching && !!debouncedSearchTerm}
            hasSearchTerm={Boolean(debouncedSearchTerm)}
          />
        )}
      </Box>

      {/* Order Detail Modal */}
      <OrderAppliedDiscountModal
        open={orderOpen}
        handleClose={handleOrderClose}
        ordersData={orderData}
        isLoading={orderLoading}
      />
    </React.Fragment>
  );
};
